import React from "react";
import { useLocation, Link } from "react-router-dom";
import { getUserDisplayName, getUserInitials } from "../../../utils/userUtils";
import {
  LayoutDashboard,
  FileText,
  Users,
  CreditCard,
  BarChart3,
  Building,
  Building2,
  Percent,
  History,
  Settings,
  LogOut,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { logout } from "../../../store/slices/authSlice";

interface SidebarProps {
  user: any;
}

const clientMenu = [
  { name: "dashboard",    path: "/client/dashboard",    icon: LayoutDashboard },
  { name: "fournisseurs", path: "/client/fournisseurs", icon: Users },
  { name: "factures",     path: "/client/factures",     icon: FileText },
  { name: "paiements",    path: "/client/paiements",    icon: CreditCard },
  { name: "analytics",   path: "/client/analytics",    icon: BarChart3 },
];

const fournisseurMenu = [
  { name: "dashboard",  path: "/fournisseur/dashboard",  icon: LayoutDashboard },
  { name: "factures",   path: "/fournisseur/factures",   icon: FileText },
  { name: "documents",  path: "/fournisseur/documents",  icon: FileText },
  { name: "paiements",  path: "/fournisseur/paiements",  icon: CreditCard },
  { name: "analytics",  path: "/fournisseur/analytics",  icon: BarChart3 },
];

const adminMenu = [
  { name: "dashboard",    path: "/admin/dashboard",    icon: LayoutDashboard },
  { name: "utilisateurs", path: "/admin/utilisateurs", icon: Users },
  { name: "clients",      path: "/admin/clients",      icon: Building },
  { name: "fournisseurs", path: "/admin/fournisseurs", icon: Building2 },
  { name: "factures",     path: "/admin/factures",     icon: FileText },
  { name: "paiements",    path: "/admin/paiements",    icon: CreditCard },
  { name: "analytics",   path: "/admin/analytics",    icon: BarChart3 },
  { name: "commissions",  path: "/admin/commissions",  icon: Percent },
  { name: "banques",      path: "/admin/banques",      icon: Building2 },
  { name: "logsAudit",    path: "/admin/logs",         icon: History },
  { name: "parametres",   path: "/admin/parametres",   icon: Settings },
];

/* ── Afflux logo ───────────────────────────────────────────────── */
const AffluxLogo: React.FC = () => (
  <div className="flex items-center gap-3">
    <div className="flex-shrink-0 bg-white rounded-lg p-1">
      <img src="/logo.png" alt="afflux" className="h-8 w-auto" />
    </div>
    <span
      className="text-xl font-extrabold tracking-tight text-white"
      style={{ letterSpacing: "-0.02em" }}
    >
      afflux
    </span>
  </div>
);

export const Sidebar: React.FC<SidebarProps> = ({ user }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();

  const getMenu = () => {
    switch (user?.role) {
      case "client":      return clientMenu;
      case "fournisseur": return fournisseurMenu;
      case "admin":
      case "super_admin": return adminMenu;
      case "finance": {
        const allowed = ["dashboard", "factures", "paiements", "analytics"];
        return adminMenu.filter(item => allowed.includes(item.name));
      }
      default: return [];
    }
  };

  const menu = getMenu();

  return (
    <div
      className="flex flex-col flex-1 overflow-hidden"
      style={{ backgroundColor: "#1E3A8A" }}
    >
      {/* ── Logo ─────────────────────────────────────────────────── */}
      <div
        className="flex items-center h-16 flex-shrink-0 px-5"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.10)" }}
      >
        <AffluxLogo />
      </div>

      {/* ── Navigation ───────────────────────────────────────────── */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {menu.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`
                group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg
                transition-all duration-150 outline-none
                ${isActive
                  ? "text-white"
                  : "text-blue-200 hover:bg-white/10 hover:text-white"
                }
              `}
              style={isActive ? { backgroundColor: "#10B981" } : undefined}
            >
              <Icon
                className={`mr-3 h-[18px] w-[18px] flex-shrink-0 transition-colors ${
                  isActive
                    ? "text-white"
                    : "text-blue-300 group-hover:text-white"
                }`}
              />
              <span className="truncate">{t(`sidebar.${item.name}`)}</span>
            </Link>
          );
        })}
      </nav>

      {/* ── User footer ──────────────────────────────────────────── */}
      <div
        className="flex-shrink-0 p-4"
        style={{ borderTop: "1px solid rgba(255,255,255,0.10)" }}
      >
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div
            className="inline-flex items-center justify-center h-9 w-9 rounded-full flex-shrink-0 text-sm font-bold text-white"
            style={{ backgroundColor: "#10B981" }}
          >
            {getUserInitials(user)}
          </div>

          {/* Name + role */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">
              {getUserDisplayName(user)}
            </p>
            <p className="text-xs text-blue-300 truncate capitalize">
              {user?.role ?? ""}
            </p>
          </div>

          {/* Logout */}
          <button
            onClick={() => dispatch(logout())}
            title="Déconnexion"
            className="flex-shrink-0 p-1.5 rounded-md text-blue-300 hover:text-white hover:bg-white/10 transition-colors"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
